
import { useEffect, useRef } from "react";
import { BrainCircuit, Briefcase } from "lucide-react";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import AnimatedCard from "./AnimatedCard";
import SectionHeader from "./common/SectionHeader";
import FeatureCard from "./features/FeatureCard";
import StatsSection from "./stats/StatsSection";

export const WhyChooseUsSection = () => {
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const animateValue = (element: HTMLElement, start: number, end: number, duration: number) => {
      let startTimestamp: number | null = null;
      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const value = Math.floor(progress * (end - start) + start);
        element.textContent = value.toString();
        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          element.textContent = end.toString();
        }
      };
      window.requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const counters = entry.target.querySelectorAll('[data-counter]');
          counters.forEach(counter => {
            const element = counter as HTMLElement;
            const value = parseInt(element.getAttribute('data-value') || '0', 10);
            animateValue(element, 0, value, 1500);
          });
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.5
    });

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  const features = [
    {
      icon: BrainCircuit,
      title: "Deep Legal Understanding",
      description: "Our team includes practicing attorneys and legal technologists who understand the unique challenges of legal work. We build AI that thinks like a lawyer.",
      bulletPoints: [
        "Legal-specific AI training & testing",
        "Solutions built for legal workflows",
        "Domain expertise in multiple practice areas"
      ],
      linkText: "Meet Our Team",
      linkUrl: "/about-us",
      animation: "slide-in-left" as const
    },
    {
      icon: Briefcase,
      title: "Practical Innovation",
      description: "We focus on practical solutions that create immediate value, not theoretical AI capabilities. Our consulting services address real problems lawyers face every day.",
      bulletPoints: [
        "Strategic AI implementation planning",
        "Customized training programs",
        "ROI-focused consulting approach"
      ],
      linkText: "Our Services",
      linkUrl: "/services",
      animation: "slide-in-right" as const,
      delay: 200
    }
  ];

  return (
    <section className="section-padding relative px-6 overflow-hidden bg-background">
      {/* Smooth gradient transitions */}
      <div className="absolute top-0 left-0 w-full h-48 bg-gradient-to-b from-[#0c1427] via-[#1a1b3a] to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-48 bg-gradient-to-t from-background to-transparent"></div>
      
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-8 mb-16">
          <div className="w-full md:w-2/3">
            <SectionHeader
              highlightedText="Why"
              title="Choose Us"
              description="We Thrive At The Intersection of Practical Innovation & Deep Legal Understanding"
            />
          </div>
          <div className="w-full md:w-1/3">
            <AnimatedCard animation="fade-in" delay={200}>
              <div className="rounded-lg overflow-hidden shadow-lg border border-white/10">
                <img 
                  src="/lovable-uploads/optimized/training-service-800w.webp" 
                  alt="Legal professionals training session" 
                  className="w-full h-auto object-cover transition-transform duration-500 hover:scale-105" 
                />
              </div>
            </AnimatedCard>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>
        
        <div ref={statsRef}>
          <StatsSection />
        </div>
        
        <AnimatedCard animation="fade-in" delay={500}>
          <div className="text-center">
            <p className="text-lg text-white/70 max-w-3xl mx-auto mb-6">
              Experience the difference our AI-focused legal consulting can make for your practice.
            </p>
            <Link to="/schedule-consultation">
              <Button variant="default" size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                Schedule a Consultation
              </Button>
            </Link>
          </div>
        </AnimatedCard>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
